import { Button, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function DeliveryDetailPage() {
	const shipmentRouteId = useNumberParam('shipmentRouteId');
	const client = useTerminalClient();
	const navigate = useNavigate();

	const { data: route } = useQuery({
		queryKey: ['shipment-route', shipmentRouteId],
		queryFn: () =>
			client.shipmentRoutes.getById.execute({
				id: shipmentRouteId!,
				populate: ['vehicle']
			}),
		enabled: !!shipmentRouteId
	});

	const { data: itemsToLoad } = useQuery({
		queryKey: ['shipment-route-items-to-load', shipmentRouteId],
		queryFn: () => client.shipments.getShipmentRouteItemsToLoadQuery.execute({ shipmentRouteId: shipmentRouteId! }),
		enabled: !!shipmentRouteId
	});

	const { data: remaining } = useQuery({
		queryKey: ['shipment-route-remaining-items', shipmentRouteId],
		queryFn: () => client.shipments.getShipmentRouteRemainingItemsToPickQuery.execute({ shipmentRouteIds: [shipmentRouteId!] }),
		enabled: !!shipmentRouteId
	});

	const { data: loadingInfo } = useQuery({
		queryKey: ['shipment-route-loading-info', shipmentRouteId],
		queryFn: () => client.shipments.getShipmentRouteLoadingInfoQuery.execute({ shipmentRouteId: shipmentRouteId! }),
		enabled: !!shipmentRouteId
	});

	const remainingMap = useMemo(() => new Map((remaining?.[0]?.missingItems ?? []).map(x => [x.stockItem!.id, x.quantityToPick])), [remaining]);
	const dateText = route?.startAt
		? new Intl.DateTimeFormat('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(route.startAt))
		: '';

	const missingCount = useMemo(() => remainingMap.size, [remainingMap]);

	const getMissingCountColor = useMemo(
		() => () => {
			if (remaining == null) {
				return 'bg-muted';
			}
			return missingCount > 0 ? 'bg-red-600' : 'bg-green-600';
		},
		[missingCount, remaining]
	);

	const startRouteLoadingMutation = useMutation({
		mutationFn: () => client.shipments.startShipmentRouteLoadingMutation.execute({ shipmentRouteId: shipmentRouteId! }),
		onSuccess: relation => navigate(`/delivery/list/${shipmentRouteId}/process/${relation?.id}`)
	});

	useValidationResultDialog([startRouteLoadingMutation.error]);

	return (
		<TerminalScreen
			showBackButton
			topBar={
				<div className="flex items-center gap-2">
					<div className={`h-4 w-4 rounded-sm ${getMissingCountColor()}`} />
					<div className="flex-1">
						<div>{route?.vehicle?.name ?? ''}</div>
						<div className="text-xs font-medium">{dateText}</div>
					</div>
				</div>
			}
		>
			<div className="h-full flex flex-col p-2">
				<div className="flex flex-col gap-2 h-full overflow-auto">
					<Table>
						<TableBody>
							<TableRow>
								<TableHead className="w-1/2">Zpracovává</TableHead>
								<TableCell className="w-1/2">{loadingInfo?.processedBy?.name ?? '-'}</TableCell>
							</TableRow>
							<TableRow>
								<TableHead className="w-1/2">Množství</TableHead>
								<TableCell className="w-1/2 font-mono">{loadingInfo?.totalCount ?? 0}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					{itemsToLoad ? (
						itemsToLoad.length === 0 ? (
							<div className="p-6 text-center text-muted-foreground">Žádné položky k naložení</div>
						) : (
							itemsToLoad.map(item => (
								<div key={`${item.stockItem!.id}`} className="bg-white border rounded p-2 flex flex-col gap-1">
									<div className="font-semibold">{item.stockItem!.item.code}</div>
									<div>{item.stockItem!.item.name}</div>
									<div className="flex justify-between">
										<div className={`${(remainingMap.get(item.stockItem!.id) ?? 0) > 0 ? 'bg-red-100 text-red-800 px-2 rounded-md' : 'opacity-0'}`}>
											Vyskladnit: {remainingMap.get(item.stockItem!.id) ?? 0}ks
										</div>
										<div>Naložit: {item.quantityToLoad ?? 0}ks</div>
									</div>
								</div>
							))
						)
					) : (
						<div className="space-y-2">
							<Skeleton className="h-20 w-full" />
							<Skeleton className="h-20 w-full" />
						</div>
					)}
				</div>

				<div className="mt-4 sticky bottom-0">
					<Button className="w-full" onClick={() => startRouteLoadingMutation.mutate()}>
						Naložit trasu
					</Button>
				</div>
			</div>
		</TerminalScreen>
	);
}
